/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Threshold, MeterSize, ThresholdDisplay } from '../utils/UNSAFE_meterTypes';
import { getClipPath, Sector } from '../utils/PRIVATE_meterUtils';
import { getAngleExtentFromDelta, getStartAngleForValue } from './CircleMeterUtils';
import { MeterProps, CircularRefLine } from './MeterCircle.types';
import { ColorProps } from '../utils/UNSAFE_interpolations/colors';

export type MeterInfo = Required<
  Pick<MeterProps, 'min' | 'max' | 'value' | 'size' | 'startAngle' | 'angleExtent'>
> & {
  section: Sector;
  isRtl: boolean;
  innerRadius: number;
};

/**
 * Returns the css variable corresponding to the default size of the meter circle.
 * @param size The size of the meter circle.
 * @returns The css variable corresponding to the default size of the meter circle.
 */
function getSizeVar(size: MeterSize): string {
  return `var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-${size}-size)`;
}

/**
 * Returns the css variable corresponding to the default track size of the meter circle.
 * @param size The size of the meter circle.
 * @returns The css variable corresponding to the default track size of the meter circle.
 */
function getTrackSizeVar(size: MeterSize): string {
  return `var(--oj-c-PRIVATE-DO-NOT-USE-meter-circle-${size}-track-size)`;
}

/**
 * Returns the calculated css variable string for the inner radius when given size and innerRadius of meter circle.
 * @param size The size of the meter circle.
 * @param innerRadius The inner radius of the meter circle.
 * @returns {string} CSS varible string for the calculated inner radius of the meter circle.
 */

export function getInnerRadius(size: MeterSize, innerRadius?: number): string {
  if (innerRadius != undefined) {
    return `(${getSizeVar(size)} * ${innerRadius / 2})`;
  }
  return `(${getSizeVar(size)} * 0.5 - ${getTrackSizeVar(size)})`;
}

/**
 * Returns the calculated css variable string for meter circle track for a given size and innerRadius of meter circle.
 * @param size The size of the meter circle.
 * @param innerRadius The inner radius of the meter circle.
 * @returns {string} CSS variable for the calculated track size of the meter circle.
 */
export function getTrackSize(size: MeterSize, innerRadius?: number): string {
  if (innerRadius != undefined) {
    return `(${getSizeVar(size)} * ${1 - innerRadius} / 2)`;
  }
  return getTrackSizeVar(size);
}

/**
 * Returns the calculated length of reference line for given size and inner radius of meter circle.
 * The length of reference line runs from the inner radius to slight outside the outer radius.
 * The length outside the outer radius depends on size of the meter circle.
 * @param size The size of the meter circle.
 * @param innerRadius The inner radius of the meter circle.
 * @returns The calculated CSS var for the length of reference line.
 */
export function getRefLineLength(size: MeterSize, innerRadius?: number): string {
  const outerExtent = { sm: '1rem', md: '0.75rem', lg: '0.5rem' };
  return `${getTrackSize(size, innerRadius)} + ${outerExtent[size]}`;
}

/**
 * Returns the array of calculated props for InnerCircle representing each threshold.
 * @param meterInfo MeterInfo object containing info about given meter circle.
 * @param thresholds thresholds array of the meter circle.
 * @param isTrackRendered Whether track is rendered.
 * @param trackColor The track color of the meter circle.
 * @param innerRadius The inner radius of the meter circle.
 * @returns Array of calculated props object for InnerCircle representing each threshold.
 */
export function getThresholdsProps(
  meterInfo: MeterInfo,
  thresholds: Threshold[],
  isTrackRendered: boolean,
  trackColor?: ColorProps['color'],
  innerRadius?: number
): Array<{
  clipPath: string;
  color?: ColorProps['color'];
  section: Sector;
  size: string;
}> {
  const {
    min,
    max,
    startAngle,
    angleExtent,
    size,
    section,
    isRtl,
    innerRadius: innerDim
  } = meterInfo;
  const trackSize = `calc(${getTrackSize(size, innerRadius)})`;
  const thresholdProps = [];
  const validThresholds = thresholds
    .filter((threshold: Threshold) => threshold.max > min && threshold.max <= max)
    .sort((a, b) => a.max - b.max);
  const numOfThreshold = validThresholds.length;
  for (let i = 0; i < numOfThreshold; i++) {
    const threshold = validThresholds[i];
    const startValue = i === 0 ? min : validThresholds[i - 1].max;
    const diff = i === 0 ? threshold.max : threshold.max - validThresholds[i - 1].max;

    let thresholdStart = getStartAngleForValue(
      startValue,
      min,
      max,
      startAngle,
      angleExtent,
      isRtl
    );
    let thresholdExtent = getAngleExtentFromDelta(diff, min, max, angleExtent);

    const { startOffset, extentOffset } = getThresholdsBuffer(
      innerDim,
      angleExtent,
      i === 0,
      threshold.max === max,
      isRtl
    );
    const clipPath = getClipPath(
      thresholdStart + startOffset,
      thresholdExtent + extentOffset,
      isRtl,
      section
    );
    thresholdProps.push({
      clipPath,
      color: threshold?.color,
      size: trackSize,
      section
    });

    if (i === numOfThreshold - 1 && threshold.max < max && isTrackRendered) {
      thresholdStart = thresholdStart + (isRtl ? 1 : -1) * thresholdExtent;
      thresholdExtent = getAngleExtentFromDelta(max - threshold.max, min, max, angleExtent);
      const { startOffset, extentOffset } = getThresholdsBuffer(
        innerDim,
        angleExtent,
        false,
        true,
        isRtl
      );
      thresholdProps.push({
        clipPath: getClipPath(
          thresholdStart + startOffset,
          thresholdExtent + extentOffset,
          isRtl,
          section
        ),
        color: trackColor,
        size: trackSize,
        section
      });
    }
  }
  return thresholdProps;
}

/**
 * Returns the calculated props for CircularReferenceLine.
 * @param meterInfo MeterInfo object containing info about the meter circle.
 * @param refLine The CircularRefLine object
 * @param innerRadius The innerRadius of the meter circle.
 * @returns An bject with calculated props for CircularReferenceLine.
 */
export function getReferenceLinesProps(
  meterInfo: MeterInfo,
  refLine: CircularRefLine,
  innerRadius?: number
): {
  radius: string;
  length: string;
  angle: number;
  color?: ColorProps['color'];
} {
  const { min, max, startAngle, angleExtent, size, isRtl } = meterInfo;
  return {
    radius: getInnerRadius(size, innerRadius),
    length: getRefLineLength(size, innerRadius),
    angle: getStartAngleForValue(refLine.value, min, max, startAngle, angleExtent, isRtl),
    color: refLine.color
  };
}

/**
 * Returns the calculated props for InnerCircle representing the track.
 * @param meterInfo MeterInfo object containing info about the meter circle.
 * @param trackColor The color of the track.
 * @param istrackRendered Whether the track is rendered.
 * @param thresholdDisplay Specifies whether the current threshold is displayed in the track, on the indicator, or if all thresholds are displayed in the track.
 * @param innerRadius The innerRadius of the meter circle.
 * @param thresholds thresholds array of the meter circle.
 * @returns An object with calculated props for InnerCircle that represents the track.
 */
export function getTrackProps(
  meterInfo: MeterInfo,
  isTrackRendered: boolean,
  thresholdDisplay: ThresholdDisplay,
  trackColor?: ColorProps['color'],
  innerRadius?: number,
  thresholds?: Threshold[]
): {
  section: Sector;
  color?: ColorProps['color'];
  clipPath: string;
  size: string;
} {
  const { startAngle, angleExtent, size, section, isRtl } = meterInfo;
  const color = !isTrackRendered
    ? 'transparent'
    : thresholdDisplay === 'all' && thresholds
    ? 'var(--oj-c-PRIVATE-DO-NOT-USE-dvt-contrast-line-color)'
    : trackColor;
  return {
    section,
    color,
    clipPath: getClipPath(startAngle, angleExtent, isRtl, section),
    size: `calc(${getTrackSize(size, innerRadius)})`
  };
}

/**
 * Returns the calculated props for InnerCircle representing the indicator.
 * @param meterInfo MeterInfo object containing info about the meter circle.
 * @param indicatorSize Relative thickness of the indicator to the track.
 * @param innerRadius The innerRadius of the meter circle.
 * @returns An object with calculated props for InnerCircle that represents the indicator.
 */
export function getIndicatorProps(
  meterInfo: MeterInfo,
  indicatorSize: number,
  innerRadius?: number
): {
  section: Sector;
  width: string;
  height: string;
  size: string;
  clipPath: string;
} {
  const { value, min, max, startAngle, angleExtent, size, section, isRtl } = meterInfo;
  const valueExtent = ((value - min) * angleExtent) / (max - min);
  const validIndicatorSize = Math.min(Math.max(0, indicatorSize), 1);
  const isHoriz = section === 'left' || section === 'right';
  const isVert = section === 'top' || section === 'bottom';
  const valueTrackSize = `calc(${getTrackSize(size, innerRadius)} * ${validIndicatorSize})`;
  const diameter = `${getSizeVar(size)} - ((1 - ${validIndicatorSize}) * ${getTrackSize(
    size,
    innerRadius
  )})`;
  const radius = `(${diameter}) / 2`;
  return {
    section,
    width: isHoriz ? `calc(${radius})` : `calc(${diameter})`,
    height: isVert ? `calc(${radius})` : `calc(${diameter})`,
    size: valueTrackSize,
    clipPath: getClipPath(startAngle, valueExtent, isRtl, section)
  };
}

/**
 * Returns the threshold offsets to create the whitespace between the thresholds.
 * @param innerRadius The innerRadius of the meter circle.
 * @param angleExtent The angleExtent of the meter circle.
 * @param firstThreshold Whether the current threshold is the first threshold.
 * @param lastThreshold  Whether the current threshold is the last threshold.
 * @param isRtl Whether the document reading mode is 'rtl'
 * @returns The offsets (startOffset and extentOffset) to add to calculated threshold startAngle and threhsold extent.
 */
function getThresholdsBuffer(
  innerRadius: number,
  angleExtent: number,
  firstThreshold: boolean,
  lastThreshold: boolean,
  isRtl: boolean
): {
  startOffset: number;
  extentOffset: number;
} {
  if (!innerRadius) {
    return { startOffset: 0, extentOffset: 0 };
  }
  const offset = 360 / (2 * Math.PI * innerRadius);
  let startOffset = (offset / 2) * (isRtl ? 1 : -1);
  let extentOffset = -1 * offset;

  if (firstThreshold && angleExtent < 360) {
    startOffset = 0;
    extentOffset = -0.5 * offset;
  }

  if (lastThreshold && angleExtent < 360) {
    extentOffset = -0.5 * offset;
  }
  return { startOffset, extentOffset };
}
